<template>
  <div class="schedule-skeleton">
    <div class="layout">
      <!-- 左侧分类导航骨架 -->
      <div class="sidebar">
        <!-- 全部赛事按钮 -->
        <div class="main-button"></div>
        
        <!-- 分类区块 -->
        <div class="category-section" v-for="i in 3" :key="'cat-'+i">
          <div class="category-header">
            <div class="category-title"></div>
            <div class="category-count"></div>
          </div>
          <div class="category-buttons">
            <div class="cat-button" v-for="j in 9" :key="'btn-'+i+'-'+j"></div>
          </div>
          <div class="expand-button"></div>
        </div>
      </div>

      <!-- 右侧内容区骨架 -->
      <div class="content">
        <!-- 日期导航骨架 -->
        <div class="header">
          <div class="date-nav">
            <div class="nav-arrow left"></div>
            <div class="date-items">
              <div class="date-item" v-for="i in 7" :key="'date-'+i">
                <div class="date-text"></div>
                <div class="day-text"></div>
              </div>
            </div>
            <div class="nav-arrow right"></div>
          </div>
          <div class="selected-info">
            <div class="selected-date"></div>
            <div class="filters">
              <div class="filter-btn" v-for="i in 2" :key="'filter-'+i"></div>
            </div>
          </div>
        </div>

        <!-- 比赛列表骨架 -->
        <div class="match-list">
          <div class="match-item" v-for="i in rows" :key="'match-'+i">
            <div class="match-header">
              <div class="league-name"></div>
              <div class="match-status"></div>
              <div class="avatars"></div>
            </div>
            <div class="match-content">
              <div class="time-box"></div>
              <div class="match-info">
                <div class="team"></div>
                <div class="score"></div>
                <div class="team"></div>
              </div>
              <div class="watch-btn"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 赛程页面骨架屏组件
 * - 匹配 Schedule 页面布局（左侧分类导航 + 右侧日期导航和比赛列表）
 */
const props = defineProps<{ rows?: number }>()
const rows = props.rows ?? 8
</script>

<style scoped lang="scss">
.schedule-skeleton {
  width: 100%;
  min-height: calc(100vh - 70px);
  background: $schedule-wrapper-bg;
  padding-left: 60px; // 为固定侧边栏留空间
  
  .layout {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    padding-top: 30px;
    gap: 20px;
  }

  // 左侧分类导航骨架
  .sidebar {
    width: 250px;
    background: $schedule-category-white;
    border: 1px solid $schedule-category-border;
    border-radius: 8px;
    height: 710px;
    padding: 20px;
    position: sticky;
    top: 70px;

    .main-button {
      width: 208px;
      height: 26px;
      background: #e8eaed;
      border-radius: 20px;
      margin: 0 auto 20px;
      animation: shimmer 1.4s ease-in-out infinite;
    }

    .category-section {
      margin-bottom: 20px;

      .category-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        .category-title {
          width: 60px;
          height: 16px;
          background: #e8eaed;
          border-radius: 4px;
          animation: shimmer 1.4s ease-in-out infinite;
        }

        .category-count {
          width: 80px;
          height: 14px;
          background: #e8eaed;
          border-radius: 4px;
          animation: shimmer 1.4s ease-in-out infinite;
        }
      }

      .category-buttons {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 5px;

        .cat-button {
          width: 65px;
          height: 26px;
          background: #f5f6fa;
          border-radius: 4px;
          animation: shimmer 1.4s ease-in-out infinite;
        }
      }

      .expand-button {
        width: 208px;
        height: 22px;
        background: #f5f6fa;
        border-radius: 4px;
        margin-top: 10px;
        animation: shimmer 1.4s ease-in-out infinite;
      }
    }
  }

  // 右侧内容区骨架
  .content {
    flex: 1;
    width: 940px;

    .header {
      width: 940px;
      height: 120px;
      background: #e8eaed;
      border-radius: 10px;
      margin-bottom: 20px;
      position: sticky;
      top: 70px;
      padding: 14px 20px;

      .date-nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 15px;

        .nav-arrow {
          width: 20px;
          height: 20px;
          background: #d0d2d6;
          border-radius: 4px;
          animation: shimmer 1.4s ease-in-out infinite;
          
          &.left, &.right {
            flex-shrink: 0;
          }
        }

        .date-items {
          display: flex;
          justify-content: space-between;
          flex: 1;
          margin: 0 20px;

          .date-item {
            text-align: center;
            flex: 1;

            .date-text {
              width: 40px;
              height: 14px;
              background: #d0d2d6;
              border-radius: 4px;
              margin: 0 auto 4px;
              animation: shimmer 1.4s ease-in-out infinite;
            }

            .day-text {
              width: 30px;
              height: 12px;
              background: #d0d2d6;
              border-radius: 4px;
              margin: 0 auto;
              animation: shimmer 1.4s ease-in-out infinite;
            }
          }
        }
      }

      .selected-info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 10px;

        .selected-date {
          width: 120px;
          height: 20px;
          background: #d0d2d6;
          border-radius: 4px;
          animation: shimmer 1.4s ease-in-out infinite;
        }

        .filters {
          display: flex;
          gap: 10px;

          .filter-btn {
            width: 80px;
            height: 30px;
            background: #d0d2d6;
            border-radius: 20px;
            animation: shimmer 1.4s ease-in-out infinite;
          }
        }
      }
    }

    .match-list {
      .match-item {
        width: 940px;
        height: 110px;
        background: $schedule-item-bg;
        border-radius: 20px;
        margin-bottom: 20px;
        overflow: hidden;

        .match-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 20px;
          border-bottom: 1px solid #eee;

          .league-name {
            width: 100px;
            height: 14px;
            background: #e8eaed;
            border-radius: 4px;
            animation: shimmer 1.4s ease-in-out infinite;
          }

          .match-status {
            width: 60px;
            height: 14px;
            background: #e8eaed;
            border-radius: 4px;
            animation: shimmer 1.4s ease-in-out infinite;
          }

          .avatars {
            width: 110px;
            height: 14px;
            background: #e8eaed;
            border-radius: 4px;
            animation: shimmer 1.4s ease-in-out infinite;
          }
        }

        .match-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 20px;

          .time-box {
            width: 72px;
            height: 32px;
            background: #f0f2f5;
            border-radius: 8px;
            animation: shimmer 1.4s ease-in-out infinite;
          }

          .match-info {
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 1;
            gap: 10px;

            .team {
              width: 120px;
              height: 16px;
              background: #e8eaed;
              border-radius: 4px;
              animation: shimmer 1.4s ease-in-out infinite;
            }

            .score {
              width: 60px;
              height: 16px;
              background: #e8eaed;
              border-radius: 4px;
              animation: shimmer 1.4s ease-in-out infinite;
            }
          }

          .watch-btn {
            width: 80px;
            height: 30px;
            background: #e8eaed;
            border-radius: 20px;
            animation: shimmer 1.4s ease-in-out infinite;
          }
        }
      }
    }
  }

  @keyframes shimmer {
    0% { opacity: 1; }
    50% { opacity: 0.6; }
    100% { opacity: 1; }
  }
}
</style>
